/**
 * Signing in again over a page whose session ended (PHP-3322): the password step, then the MFA step
 * (an authenticator code or a recovery code). It only ever continues the page for the *same* user; a
 * different account is reported, never adopted (the organization and every cache here belong to the
 * first user).
 */
import { auth, session } from "./api";
import { ApiError } from "./http";
import type { TokensOut } from "./types";

export type ReauthStep =
  | { step: "done" }
  | { step: "mfa"; challengeToken: string }
  /** Signed in fine, but as someone else than this page belongs to. Nothing was switched. */
  | { step: "other-user"; email: string };

/** Identity's refusals at sign-in in words for the person (the Login page and the re-login prompt). */
export function loginErrorMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  switch (err.reason) {
    case "INVALID_CREDENTIALS":
      return "That e-mail and password don't match.";
    case "ACCOUNT_LOCKED":
      return `Too many attempts. Try again in ${Math.ceil((err.retryAfterSeconds ?? 60) / 60)} minute(s).`;
    case "MFA_CODE_INVALID":
      return "That code isn't valid. Codes change every 30 seconds.";
    default:
      return null;
  }
}

/** Any failure of the steps below, for the prompt. */
export function reauthMessage(err: unknown): string {
  return loginErrorMessage(err) ?? (err instanceof Error ? err.message : "Something went wrong. Try again.");
}

function finish(tokens: TokensOut): ReauthStep {
  return session.completeReauth(tokens) === "ok"
    ? { step: "done" }
    : { step: "other-user", email: tokens.user.email };
}

/** Step 1. Throws the ApiError when Identity refuses (wrong password...). */
export async function reauthWithPassword(email: string, password: string): Promise<ReauthStep> {
  const result = await auth.login(email.trim(), password);
  if (result.status === "mfa_required" && "challenge_token" in result)
    return { step: "mfa", challengeToken: result.challenge_token };
  return finish(result as TokensOut);
}

/** Step 2. */
export async function reauthWithMfa(
  challengeToken: string,
  code: string,
  isRecoveryCode: boolean,
): Promise<ReauthStep> {
  const trimmed = code.trim();
  return finish(
    await auth.loginMfa(challengeToken, isRecoveryCode ? { recovery_code: trimmed } : { code: trimmed }),
  );
}

/** "Back" from the different-account step: sign that account out again, so the right one can sign in. */
export function discardOtherUser(): Promise<void> {
  return session.discardOtherUser();
}
